import * as d3 from 'd3-selection'
import shape from './shape/index.js'
import { transition } from 'd3-transition'
import current from '../current-day.js'
import { extent } from 'd3-array'
import descendantsWrapper from './descendants-wrapper.js'
import offScreenIndicator from './offscreen-indicator.js'
import * as util from '../util.js'

export default function (selection, gantt) {
  selection.each(function (data) {
    let xScale = gantt.xScale
      , tree = gantt.tree
      , t = transition().duration(tree.el.select('.tree').classed('transitions') ? tree.transitionTimeout : 0)
      , svg = d3.select(this).selectAll('svg')
                             .data(function (d) {
                               var wrappers = d._gantt && d._gantt.descendantWrappers || {}

                               // Check if we have enough info to draw this shape... If we don't have enough information
                               // just return an empty object so the svg is still drawn
                               if ((d.type === 'milestone' && !d.endDate) || (d.type !== 'milestone' && (!d.startDate || !d.endDate))) {
                                 // If it's a milestone without an end date, or a task/initiative without start and end dates
                                 return [{
                                   _layout: d._layout,
                                   _gantt: {
                                     // Store these properties in case a parent node set it on a descendant, see below
                                     lastDescendant: d._gantt && d._gantt.lastDescendant,
                                     descendantWrappers: wrappers // Set wrappers in case a parent node is showing children, and one of those children is this node we can't draw b/c of missing data
                                   }
                                 }]
                               }

                               var allDescendants = tree._descendants(d._layout)
                                                        .map(d => tree.get(d.id))
                                                        .map(d => [d.startDate, d.endDate])
                                 , _extent = extent([].concat(...allDescendants))
                                 , shapeWidth = xScale(d.endDate) - (xScale(d.startDate) || 0)

                               if (d._layout.children) {
                                 // This node is expanded, so it will have a descendants wrapper
                                 // Find all the *visible* descendants
                                 var descendants = tree._descendants(d._layout, 'children')
                                                       .map(d => tree.get(d.id))
                                   , descendantsExtent = util.extent(descendants)
                                   , x = Math.max(-10, xScale(descendantsExtent[0])) // Prevent incredible large negative values which we can't see
                                   , width = Math.max(0, xScale(descendantsExtent[1]) - x)
                                   , y1 = tree.options.height - gantt.dimensions.node.withChildren.margin.bottom
                                   , height = tree.options.height - y1

                                 // Store this wrapper data on this node with a shorter height since it will be drawn straight
                                 // down from the tips
                                 wrappers[d.id] = [{
                                   x1: x, x2: x, y1, y2: y1 + height, width
                                 }, {
                                   x1: x + width, x2: x + width, y1, y2: y1 + height, width
                                 }]

                                 // Now make sure each descendant knows to draw these descendant wrapper lines
                                 descendants.filter((descendant, i) => i > 0) // Ignore this node
                                            .forEach((descendant, i, all) => {
                                              if (!descendant._gantt) {
                                                descendant._gantt = {
                                                  descendantWrappers: {}
                                                }
                                              }

                                              // Sets the vertical edges
                                              descendant._gantt.descendantWrappers[d.id] = [{
                                                x1: x, x2: x, y1: 0, y2: tree.options.height
                                              }, {
                                                x1: x + width, x2: x + width, y1: 0, y2: tree.options.height
                                              }]

                                              // See if it's the last descendant
                                              if (i == all.length - 1) {
                                                // Now update if this descendant doesn't have `lastDescendant` data or we're update the dimensions
                                                if (!descendant._gantt.lastDescendant || descendant._gantt.lastDescendant.forNodeId === d.id) {
                                                  // Set the last descendant
                                                  descendant._gantt.lastDescendant = {
                                                    forNodeId: d.id,
                                                    x1: x,
                                                    x2: x + width,
                                                    y1: gantt.tree.options.height - gantt.dimensions.stroke / 2,
                                                    y2: gantt.tree.options.height - gantt.dimensions.stroke / 2
                                                  }
                                                }
                                              }
                                            })
                               } else {
                                // Remove it so the lines are removed
                                delete wrappers[d.id]
                               }

                               if (d.endDate && d.startDate) {
                                 // Initiative/task with dates. Determine the foreground status width based on `percentComplete`
                                 var foregroundWidth = shapeWidth * d.percentComplete
                               }

                               // Store all gantt related drawing data on the node
                               d._gantt = d._gantt || {}
                               d._gantt.x = (xScale(d.startDate) || 0) - xScale(_extent[0]) // Start position for the actual drawing, not its descendants
                               d._gantt.width = shapeWidth
                               d._gantt.foregroundWidth = foregroundWidth || shapeWidth // if it's 0, then it's actually going to take up the entire shape
                               d._gantt.leftOffset = _extent[0] < d.startDate ? -3 : 0
                               d._gantt.rightOffset = _extent[1] > d.endDate ? -3 : 0
                               d._gantt.hasChildren = !!tree.children(d).length
                               d._gantt.descendantsExtent = _extent // Full extent with descendants. This can be larger than the node extent
                               d._gantt.extendedDescendants = (d.startDate && d.endDate) && (_extent[0].getTime() !== d.startDate.getTime() || _extent[1].getTime() !== d.endDate.getTime()) // Set if there are descendants overflowing this node's extent
                               d._gantt.type = d.type
                               d._gantt.descendantWrappers = wrappers

                               return [d]
                             })
                             .enter()
                             .append('svg')
                             .on('click', function (e, d) {
                               /* This is a total hack for https://github.com/SpiderStrategies/Scoreboard/issues/17780
                                *
                                * Putting the click handlers on the actual shapes breaks MS Edge. It doesn't always fire events with
                                * task drawings because of the svg > use > path layout.
                                * This approach puts a capturing event on the outer svg. We then determine if the user
                                * clicked on the foreground shape by doing math with the SVG layout data
                                */
                               if (!d._gantt.descendantsExtent) {
                                 // They clicked a node we couldn't draw, so we have nothing to show
                                 return
                               }

                               let scale = this.getBoundingClientRect().width / parseInt(d3.select(this).style('width'))
                                 , x = e.pageX - (document.documentElement.scrollLeft ? document.documentElement.scrollLeft : document.body.scrollLeft)
                                 , layerX = (x - this.getBoundingClientRect().left) * (1 / scale) // Where they clicked within the SVG
                                 , x1 = d._gantt.x + (xScale(d._gantt.descendantsExtent[0]) || 0)
                                 , x2 = x1 + d._gantt.width

                               if (d.type === 'milestone') {
                                 var width = gantt.dimensions.milestone.height / 2

                                 if (layerX >= x2 - width && layerX <= x2 + width) {
                                   gantt.emit('click', e, this, d.id)
                                   e.preventDefault()
                                 }
                               } else {
                                 // See if they clicked in a task
                                 if (layerX >= x1 && layerX <= x2) {
                                   gantt.emit('click', e, this, d.id)
                                   e.preventDefault()
                                 }
                               }
                             }, true)
                             .merge(d3.select(this).selectAll('svg'))
                               .attr('width', '100%')
                               .attr('height', tree.options.height)
                               .call(current, xScale, gantt.tree.options.height, gantt.today)
                               .call(descendantsWrapper, gantt)
                               .filter(d => d._gantt.width) // Filter the svg so we only draw if we have gantt data to draw

    var defs = svg.selectAll('defs')
                  .data(d => [d])
                  .enter()
                  .append('defs')
                  .merge(svg.selectAll('defs'))

    svg.call(offScreenIndicator, xScale, gantt.dimensions)

    var _items = svg.selectAll('g.gantt-item')
                    .data(d => [d])
      , items = _items.enter()
                      .append('g')
                        .merge(_items)
                        .attr('class', d => `gantt-item ${d.state} ${d.state}-${d.scheduleStatus}`)
                        .attr('transform', function (d) {
                          var x = xScale(d._gantt.descendantsExtent[0]) || 0 // This gantt-item x position will start at the earliest date based on all descendants
                          return `translate(${x}, 0)`
                        })
                        .call(shape, defs, gantt)

    _items.exit().remove()


  })
}
